import React from "react";
import { Link } from "react-router-dom";
import {
  Github,
  Linkedin,
  GraduationCap,
  Code,
  MapPin,
  Calendar,
} from "lucide-react";
import FadingLine from "../Components/FadingLine";
import Footer from "../Components/Footer";
import AnimatedSection from "../Components/AnimatedSection";

function About() {
  const experiences = [
    {
      timePeriod: "APR — MAY 2024",
      title: "System Design Engineer",
      company: "University of South Australia: System Design Studio",
      description:
        "Identified Key Business Essences for the given case study and designed a system decomposition diagram. Designed system interface, component validations and created multiple incremental behaviour diagrams for the given case study.",
      technologies: ["UML", "System Design", "Requirements Analysis"],
      type: "academic",
    },
    {
      timePeriod: "SEP — OCT 2024",
      title: "Scrum Master & Project Developer",
      company: "University of South Australia: Project Studio",
      description:
        "Developed, maintained, and shipped production code for client websites. Required running a team to develop a website for a client and overseeing the project and help with development.",
      technologies: [
        "HTML",
        "CSS",
        "JavaScript",
        "SQL",
        "Agile",
        "Team Leadership",
      ],
      type: "academic",
    },
  ];

  const personalInfo = {
    name: "Indralal Rimal",
    location: "Adelaide, South Australia",
    education: "Bachelor of Information Technology (Software Development)",
    university: "University of South Australia",
    expectedGraduation: "2025",
    gpa: "5.88",
    coursework: [
      "Data Structures and Algorithms (Java)",
      "Web Development",
      "Data-driven Web Technology",
      "System Design Studio",
      "Project Studio",
      "Programming Principles & OOP (Python)",
      "Design Patterns with C++",
    ],
  };

  const skills = {
    languages: ["JavaScript", "Python", "Java", "C++", "HTML", "CSS", "SQL"],
    frameworks: ["React", "Node.js", "Express"],
    tools: ["Git", "VS Code", "MySQL", "Firebase"],
    concepts: [
      "OOP",
      "Data Structures",
      "Algorithms",
      "System Design",
      "Agile/Scrum",
    ],
  };

  const interests = [
    "Gaming with friends - enhances problem-solving and strategic thinking",
    "Travel - broadens perspective and adaptability to new environments",
    "Small coding projects - experimenting with new technologies and concepts",
  ];

  const ExperienceCard = ({ experience }) => (
    <div className="bg-gray-800 rounded-xl p-6 hover:bg-gray-700 transition-all duration-300 border-l-4 border-green-500">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-white mb-1">
            {experience.title}
          </h3>
          <h4 className="text-green-400 font-medium mb-2">
            {experience.company}
          </h4>
        </div>
        <span className="text-gray-400 text-sm font-medium bg-gray-700 px-3 py-1 rounded-full">
          {experience.timePeriod}
        </span>
      </div>

      <p className="text-gray-300 mb-4 leading-relaxed">
        {experience.description}
      </p>

      <div className="flex flex-wrap gap-2">
        {experience.technologies.map((tech, index) => (
          <span
            key={index}
            className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm font-medium"
          >
            {tech}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 animate-fadeIn">
      {/* Header */}
      <div className="text-center py-6">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
          About Me
        </h1>
        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
          Get to know me better - my background, experience, and what drives me
          as a developer
        </p>
      </div>

      <FadingLine />

      <div className="max-w-4xl mx-auto px-4 py-10 animate-slideUp">
        {/* Personal Introduction */}
        <AnimatedSection>
          <div className="bg-gray-800 rounded-xl p-8 mb-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <Code className="w-8 h-8 text-green-400" />
                  {personalInfo.name}
                </h2>

                <div className="flex flex-col sm:flex-row gap-4 mb-6 text-gray-400">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{personalInfo.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Graduating {personalInfo.expectedGraduation}</span>
                  </div>
                </div>

                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p>
                    Hi, I'm Indra, a final-year Software Development student at
                    UniSA. My passion lies in building user-centric web
                    applications. I love coding and am driven to create
                    functional and intuitive solutions that truly benefit
                    others.
                  </p>

                  <p>
                    I don't specialise in any particular technology, but I enjoy
                    learning new things and applying them to my projects. My
                    goal is to become an AI Engineer, but for now I'll be
                    sticking to web development and I am always looking for
                    opportunities to grow and improve my skills.
                  </p>

                  <p>
                    In my spare time, I enjoy gaming with friends, which hones
                    my problem-solving and strategic thinking. I also have a
                    passion for travel, broadening my perspective and
                    adaptability to new environments. I also enjoy making small
                    coding projects, which allows me to experiment with new
                    technologies and concepts.{" "}
                    <Link
                      to="/projects"
                      className="text-green-400 hover:text-green-300 transition-colors"
                    >
                      Find out more about the projects I've built.
                    </Link>
                  </p>
                </div>

                {/* Social Links */}
                <div className="flex gap-4 mt-6">
                  <a
                    href="https://github.com/indr10"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors transition-transform hover:scale-105 hover:opacity-100 opacity-80"
                    aria-label="GitHub Profile"
                  >
                    <Github className="w-5 h-5 " />
                    <span>GitHub</span>
                  </a>
                  <a
                    href="https://www.linkedin.com/in/indra-rimal/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors transition-transform hover:scale-105 hover:opacity-100 opacity-80"
                    aria-label="LinkedIn Profile"
                  >
                    <Linkedin className="w-5 h-5" />
                    <span>LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Education */}
        <AnimatedSection delay={0.1}>
          <div className="bg-gray-800 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <GraduationCap className="w-7 h-7 text-green-400" />
              Education
            </h3>

            <div className="space-y-4">
              <div>
                <h4 className="text-xl font-semibold text-white">
                  {personalInfo.education}
                </h4>
                <p className="text-green-400 font-medium">
                  {personalInfo.university}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mt-2 text-gray-400">
                  <span>
                    Expected Graduation: {personalInfo.expectedGraduation}
                  </span>
                  <span>GPA: {personalInfo.gpa}</span>
                </div>
              </div>

              <div>
                <h5 className="text-lg font-semibold text-white mb-3">
                  Relevant Coursework:
                </h5>
                <div className="grid sm:grid-cols-2 gap-2">
                  {personalInfo.coursework.map((course, index) => (
                    <span key={index} className="text-gray-300 text-sm">
                      • {course}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Skills */}
        <AnimatedSection delay={0.2}>
          <div className="bg-gray-800 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">
              Technical Skills
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(skills).map(([category, items]) => (
                <div key={category}>
                  <h4 className="text-lg font-semibold text-green-400 mb-3 capitalize">
                    {category.replace(/([A-Z])/g, " $1").trim()}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {items.map((skill, index) => (
                      <span
                        key={index}
                        className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm font-medium transition-transform hover:scale-105"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Experience */}
        <AnimatedSection delay={0.3}>
          <div className="bg-gray-800 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">Experience</h3>
            <div className="space-y-6">
              {experiences.map((experience, index) => (
                <ExperienceCard key={index} experience={experience} />
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Interests */}
        <AnimatedSection delay={0.4}>
          <div className="bg-gray-800 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6">
              Interests & Hobbies
            </h3>
            <div className="space-y-3">
              {interests.map((interest, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300">{interest}</p>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
      <Footer />
    </div>
  );
}

export default About;
